import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import MaynLayout from '../../Core/MainLayout';
import PageLayout from '../../Core/PageLayout';
import TitleBlock from '../../Core/TitleBlock';
import Dashboard from '../../Core/Dashboard';
import ActivityLogIcon from '../../Icons/ActivityLog';
import Progress from '../../Core/Progress';
import {
  isLoadingSelector, isShowSnackbar,
  snackbarType, snackbarText, placesSelector, employeesSelector,
} from '../../../store/settings/selectors';
import {
  loadPlace, loadEmployees,
} from '../../../store/settings/actions';
import Filter from './filter';
import Table from './table';
import { getCompanyActivityLog } from '../../../api';

import styles from './activity.module.scss';

const useStyles = makeStyles(() => ({
  error: {
    background: '#de4343',
    color: '#fff',
  },
  success: {
    background: '#3bc39e',
    color: '#fff',
  },
}));

const ACTIVITY_LOG_PER_PAGE = 40;

const initialActivityLog = {
  data: [],
  current_page: 1,
  per_page: ACTIVITY_LOG_PER_PAGE,
  last_page: 0,
  total: 0,
};

export default function ActivityLog() {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [employees, setEmployees] = useState([]);
  const [places, setPlaces] = useState([]);
  const [actions, setActions] = useState([]);
  const [inputValues, setInputValues] = useState({
    from: {},
    employee: '',
    place: '',
    action: '',
  });

  const [activityLog, setActivityLog] = useState(initialActivityLog);
  const [isActivityLogLoading, setIsActivityLogLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isLoading = useSelector(isLoadingSelector);
  const isSnackbar = useSelector(isShowSnackbar);
  const typeSnackbar = useSelector(snackbarType);
  const textSnackbar = t(useSelector(snackbarText));
  const placesArr = useSelector(placesSelector);
  const { users: employeesArr } = useSelector(employeesSelector);

  useEffect(() => {
    dispatch(loadPlace(id));
    dispatch(loadEmployees(id));
  }, [dispatch, id]);

  const getHtmlNotes = (notes) => {
    if (!notes) {
      return ''
    }
    const lines = notes.split(';')
    const html = `
      <div>
        ${
          lines.map((line) => (
            `<div>
              ${line.replaceAll('null', '')}
            </div>`
          )).join('')
        }
      </div>
    `
    return html
  }

  const normalizeData = item => {
    if (item.action === 'Employee Updated') {
      return {
        ...item,
        notes: getHtmlNotes(item.notes),
      }
    }
    return item
  }

  const getFiltersData = useCallback((page = 1) => {
    return {
      date_from: inputValues.from.startDate ? moment(inputValues.from.startDate).format('YYYY-MM-DD') : null,
      date_to: inputValues.from.endDate ? moment(inputValues.from.endDate).format('YYYY-MM-DD') : null,
      user_id: inputValues.employee || null,
      place_id: inputValues.place || null,
      action: inputValues.action || null,
      page,
      per_page: ACTIVITY_LOG_PER_PAGE,
    };
  }, [inputValues]);

  const loadActivityLog = useCallback(async (page = 1, append = false) => {
    if (!id) return;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsActivityLogLoading(true);
    }

    const data = getFiltersData(page);
    const res = await getCompanyActivityLog(id, data);

    setActivityLog((prevState) => {
      const nextData = Array.isArray(res?.data) ? res.data : [];
      const data = append ? [...prevState.data, ...nextData] : nextData;
      return {
        ...initialActivityLog,
        ...res,
        data: data.map(normalizeData),
      };
    });
    setActions(Array.isArray(res?.actions) ? res.actions.map(a => ({id: a, name: t(a)})) : []);

    if (append) {
      setIsLoadingMore(false);
    } else {
      setIsActivityLogLoading(false);
    }
  }, [id, getFiltersData]);

  useEffect(() => {
    loadActivityLog(1, false);
  }, [loadActivityLog]);

  const handleLoadMore = useCallback(() => {
    if (isActivityLogLoading || isLoadingMore) return;
    if (!activityLog.last_page || activityLog.current_page >= activityLog.last_page) return;
    loadActivityLog(activityLog.current_page + 1, true);
  }, [activityLog, isActivityLogLoading, isLoadingMore, loadActivityLog]);

  useEffect(() => {
    setEmployees(employeesArr.map((item) => ({ fullName: `${item.name} ${item.surname}`, ...item })));
  }, [employeesArr]);

  useEffect(() => {
    setPlaces(placesArr);
  }, [placesArr]);

  const handleInputChange = (event) => {
    if (event.target) {
      const { name, value } = event.target;
      setInputValues({ ...inputValues, [name]: value });
    } else if (event.endDate) {
      setInputValues({
        ...inputValues,
        from: {
          startDate: event.startDate,
          endDate: event.endDate,
        },
      });
    }
  };

  return (
    <MaynLayout>
      <Dashboard>
        <TitleBlock
          title={t('Activity Log')}
        >
          <ActivityLogIcon viewBox='0 0 26 11' fill='rgba(226,235,244,0.85)' />
        </TitleBlock>
        <PageLayout>
          {
            isLoading ? <Progress />
              : (
                <>
                  <Filter
                    inputValues={inputValues}
                    handleInputChange={handleInputChange}
                    style={styles}
                    places={places}
                    actions={actions}
                    employees={employees}
                    t={t}
                  />
                  <Table
                    style={styles}
                    activityLog={activityLog.data}
                    places={placesArr}
                    t={t}
                    isLoading={isLoading || isActivityLogLoading}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={handleLoadMore}
                  />
                </>
              )
          }
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            ContentProps={{
              classes: {
                root: typeSnackbar === 'error' ? classes.error : classes.success,
              },
            }}
            severity='error'
            open={isSnackbar}
            message={textSnackbar}
            key='rigth'
          />
        </PageLayout>
      </Dashboard>
    </MaynLayout>
  );
}
